import React from "react"
import { TFunction } from 'i18next';
import { 
  DeleteDocument,
  HealthCalendar,
  ViewList as ViewListIcon,
  Assignment
} from '../../icons';
import { browserHistory } from 'react-router';
import Authority from "../../util/Authority";
import DnsOutlinedIcon from '@material-ui/icons/DnsOutlined';
import DoneIcon from '@material-ui/icons/Done';
import Edit from '@material-ui/icons/Edit';
import EventAvailableIcon from '@material-ui/icons/EventAvailable';

// Enums for status types
export enum CustomerStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive'
}

export enum ScheduleStatus {
  PENDING_APPROVAL = 'PENDING_APPROVAL',
  REJECTED = 'REJECTED',
  APPROVED = 'APPROVED',
  IN_PROGRESS = 'IN_PROGRESS',
  DONE = 'DONE',
  NOT_ATTENDED = 'NOT_ATTENDED',
  WITHOUT_ATTENTION = 'WITHOUT_ATTENTION',
  CANCELLED = 'CANCELLED',
  FINISHED_SYSTEM = 'FINISHED_SYSTEM'
}

export enum AppointmentStatus {
  CREATED = 'CREATED',
  SCHEDULES_ASSIGNED = 'SCHEDULES_ASSIGNED',
  PARTIAL_ASSIGNED = 'PARTIAL_ASSIGNED',
  CANCELED = 'CANCELED',
  ENDED = 'ENDED',
  OVERDUE = 'OVERDUE',
  APROVEED = 'APROVEED',
  REJECT = 'REJECT'
}

export enum RecurrenceType {
  DAY = 'DAY',
  WEEK = 'WEEK',
  MONTH = 'MONTH',
  INTRADAY = 'INTRADAY'
}

export enum ProximityLevel {
  RED = 1,
  YELLOW = 2,
  GREEN = 3
}

// Interface for status options
interface StatusOption {
  id: string | number;
  value: string;
}

// Interface for translated status lists
interface TranslatedStatusLists {
  customerStatusList: StatusOption[];
  scheduleStatusList: StatusOption[];
  appointmentStatusList: StatusOption[];
  dayAndWeekList: StatusOption[];
  proximityList: StatusOption[];
}

// Interface for action configuration
interface ActionConfig {
  key: string;
  tooltip: string;
  icon: React.ReactNode;
  color: string;
  authority?: string;
  onClick: () => void;
  show: boolean;
}

// Base structure for status lists
const createStatusList = (items: StatusOption[]): StatusOption[] => [
  { id: '', value: '' },
  ...items.map(item => ({ id: item.id, value: item.value }))
];

// Customer status options
export const CUSTOMER_STATUS_OPTIONS: StatusOption[] = [
  { id: CustomerStatus.ACTIVE, value: 'customer.active' },
  { id: CustomerStatus.INACTIVE, value: 'customer.inactive' }
];

// Schedule status options
export const SCHEDULE_STATUS_OPTIONS: StatusOption[] = [
  { id: ScheduleStatus.PENDING_APPROVAL, value: 'scheduleDetail.state.PENDING_APPROVAL' },
  { id: ScheduleStatus.REJECTED, value: 'scheduleDetail.state.REJECTED' },
  { id: ScheduleStatus.APPROVED, value: 'scheduleDetail.state.APPROVED' },
  { id: ScheduleStatus.IN_PROGRESS, value: 'scheduleDetail.state.IN_PROGRESS' },
  { id: ScheduleStatus.DONE, value: 'scheduleDetail.state.DONE' },
  { id: ScheduleStatus.NOT_ATTENDED, value: 'scheduleDetail.state.NOT_ATTENDED' },
  { id: ScheduleStatus.WITHOUT_ATTENTION, value: 'scheduleDetail.state.WITHOUT_ATTENTION' },
  { id: ScheduleStatus.CANCELLED, value: 'scheduleDetail.state.CANCELLED' },
  { id: ScheduleStatus.FINISHED_SYSTEM, value: 'scheduleDetail.state.FINISHED_SYSTEM' }
];

// Appointment status options
export const APPOINTMENT_STATUS_OPTIONS: StatusOption[] = [
  { id: AppointmentStatus.CREATED, value: 'appointment.table.status.CREATED' },
  { id: AppointmentStatus.SCHEDULES_ASSIGNED, value: 'appointment.table.status.SCHEDULES_ASSIGNED' },
  { id: AppointmentStatus.CANCELED, value: 'appointment.table.status.CANCELLED' },
  { id: AppointmentStatus.APROVEED, value: 'status.appointmentItemRequest.APROVEED' },
  { id: AppointmentStatus.REJECT, value: 'scheduleDetail.state.REJECTED' }
];

// Recurrence type options
export const RECURRENCE_TYPE_OPTIONS: StatusOption[] = [
  { id: RecurrenceType.DAY, value: 'recurrenceTypes.daily' },
  { id: RecurrenceType.WEEK, value: 'recurrenceTypes.weekly' },
  { id: RecurrenceType.MONTH, value: 'recurrenceTypes.monthly' },
  { id: RecurrenceType.INTRADAY, value: 'recurrenceTypes.intraday' }
];

// Proximity options
export const PROXIMITY_OPTIONS: StatusOption[] = [
  { id: ProximityLevel.RED, value: 'appointment.table.filters.red' },
  { id: ProximityLevel.YELLOW, value: 'appointment.table.filters.yellow' },
  { id: ProximityLevel.GREEN, value: 'appointment.table.filters.green' }
];

// Helper function to get translated status lists
export const getTranslatedStatusLists = (t: TFunction): TranslatedStatusLists => ({
  customerStatusList: createStatusList(CUSTOMER_STATUS_OPTIONS.map(item => ({
    ...item,
    value: t(item.value)
  }))),
  
  scheduleStatusList: createStatusList(SCHEDULE_STATUS_OPTIONS.map(item => ({
    ...item,
    value: t(item.value)
  }))),
  
  appointmentStatusList: createStatusList(APPOINTMENT_STATUS_OPTIONS.map(item => ({
    ...item,
    value: t(item.value)
  }))),
  
  dayAndWeekList: createStatusList(RECURRENCE_TYPE_OPTIONS.map(item => ({
    ...item,
    value: t(item.value)
  }))),
  
  proximityList: createStatusList(PROXIMITY_OPTIONS.map(item => ({
    ...item,
    value: t(item.value)
  })))
});

// Helper function to create action configurations
export const createActionConfigs = (
  appointment: any,
  index: number,
  t: TFunction,
  company_partnership_id: string | null,
  handlers: {
    onSelectAppointmentList: (id: string) => void;
    getAcceptRejectPracticeItems: (id: string) => void;
    redirectToCancelView: (id: string) => void;
    getPracticeItemRequest: (id: string) => void;
  }
): ActionConfig[] => [
  {
    key: 'view',
    tooltip: t('appointment.viewAppointment'),
    icon: <Assignment />,
    color: 'primary',
    authority: 'appointment_view,appointment_view_related',
    onClick: () => {
      handlers.onSelectAppointmentList(appointment.appointmentId);
      browserHistory.push({
        state: { mode: 'view' },
        pathname: `/solicitudes/${appointment.appointmentId}`
      });
    },
    show: true
  },
  {
    key: 'edit-agenda',
    tooltip: t('appointment.editCalendar'),
    icon: <HealthCalendar />,
    color: 'success',
    authority: 'appointment_edit_agenda',
    onClick: () => {
      handlers.onSelectAppointmentList(appointment.appointmentId);
      browserHistory.push({
        state: { mode: 'edit', authority: 'appointment_edit_agenda' },
        pathname: `/appointments/${appointment.appointmentId}/edit/agenda`
      });
    },
    show: Authority.hasPermission('appointment_edit_agenda') && 
          [AppointmentStatus.SCHEDULES_ASSIGNED, AppointmentStatus.PARTIAL_ASSIGNED].includes(appointment.appointmentStatus)
  },
  {
    key: 'accept-reject',
    tooltip: t('appointment.actions.acceptReject'),
    icon: (
      <span className="icon-with-overlay">
        <ViewListIcon className="principal-icon" />
        <DoneIcon className="overlay-icon" />
      </span>
    ),
    color: 'primary',
    authority: 'appointment_item_request_acept_reject',
    onClick: () => handlers.getAcceptRejectPracticeItems(appointment.appointmentId),
    show: Authority.hasPermission('appointment_item_request_acept_reject') && 
          [AppointmentStatus.CREATED, AppointmentStatus.PARTIAL_ASSIGNED].includes(appointment.appointmentStatus) &&
          appointment.hasItemsPending === true
  },
  {
    key: 'edit',
    tooltip: t('appointment.actions.edit'),
    icon: <Edit />,
    color: 'success',
    authority: 'appointment_edit',
    onClick: () => {
      handlers.onSelectAppointmentList(appointment.appointmentId);
      browserHistory.push({
        state: { mode: 'edit' },
        pathname: `/solicitudes/${appointment.appointmentId}`
      });
    },
    show: Authority.hasPermission('appointment_edit') && 
          company_partnership_id == null && 
          ![AppointmentStatus.CREATED, AppointmentStatus.CANCELED, AppointmentStatus.ENDED, AppointmentStatus.OVERDUE].includes(appointment.appointmentStatus)
  },
  {
    key: 'edit-created',
    tooltip: t('appointment.actions.edit'),
    icon: <Edit />,
    color: 'success',
    authority: 'appointment_edit',
    onClick: () => {
      handlers.onSelectAppointmentList(appointment.appointmentId);
      browserHistory.push({
        state: { mode: 'edit' },
        pathname: `/appointments/${appointment.appointmentId}/edit/agenda`,
      });
    },
    show: Authority.hasPermission('appointment_edit') && 
          company_partnership_id == null && 
          appointment.appointmentStatus === AppointmentStatus.CREATED
  },
  {
    key: 'cancel-created',
    tooltip: t('appointment.actions.cancel'),
    icon: <DeleteDocument className="svg-cancel-agenda" />,
    color: 'danger',
    authority: 'appointment_cancel',
    onClick: () => handlers.redirectToCancelView(appointment.appointmentId),
    show: Authority.hasPermission('appointment_cancel') && 
          company_partnership_id == null && 
          appointment.appointmentStatus === AppointmentStatus.CREATED
  },
  {
    key: 'assign',
    tooltip: t('appointment.actions.assignAgenda'),
    icon: <HealthCalendar className="svg-assign-agenda" />,
    color: 'violet',
    authority: 'appointment_assign',
    onClick: () => {
      handlers.onSelectAppointmentList(appointment.appointmentId);
      browserHistory.push({
        state: { mode: 'assign_agenda' },
        pathname: `/appointments/${appointment.appointmentId}/edit/agenda`,
      });
    },
    show: Authority.hasPermission('appointment_assign') && 
          [AppointmentStatus.CREATED, AppointmentStatus.PARTIAL_ASSIGNED].includes(appointment.appointmentStatus) &&
          appointment.hasItemsConfirmed === true
  },
  {
    key: 'cancel',
    tooltip: t('appointment.actions.cancel'),
    icon: <DeleteDocument className="svg-cancel-agenda" />,
    color: 'danger',
    authority: 'appointment_cancel',
    onClick: () => handlers.redirectToCancelView(appointment.appointmentId),
    show: Authority.hasPermission('appointment_cancel') && 
          company_partnership_id == null && 
          ![AppointmentStatus.CANCELED, AppointmentStatus.CREATED, AppointmentStatus.OVERDUE, AppointmentStatus.ENDED].includes(appointment.appointmentStatus)
  },
  {
    key: 'finish-system',
    tooltip: t('schedule.finished.title'),
    icon: <EventAvailableIcon className="svg-cancel-agenda" />,
    color: 'danger',
    authority: 'end_schedule_by_system',
    onClick: () => {
      browserHistory.push({
        state: { mode: 'view', action: "finished_system" },
        pathname: `/solicitudes/${appointment.appointmentId}`,
      });
    },
    show: Authority.hasPermission('end_schedule_by_system') && 
          company_partnership_id == null && 
          [AppointmentStatus.SCHEDULES_ASSIGNED, AppointmentStatus.OVERDUE].includes(appointment.appointmentStatus)
  },
  {
    key: 'list-practice',
    tooltip: t('appointment.actions.list.practice'),
    icon: <DnsOutlinedIcon />,
    color: 'success',
    onClick: () => handlers.getPracticeItemRequest(appointment.appointmentId),
    show: true
  }
]; 